import { useMemo, useState, type ChangeEvent } from 'react';
import { AlertTriangle, Ban, FileUp, Fingerprint, MonitorCheck, Pencil, Plus, Save } from 'lucide-react';
import {
  Button,
  DataTable,
  EmptyState,
  ErrorState,
  FormField,
  FormGrid,
  Loading,
  Modal,
  PageHeader,
  Select,
  StatusBadge,
  TextField,
  Textarea,
  useToast,
  type BadgeTone,
  type Column,
} from '@/components';
import { ORGANIZATION_TYPES, type OrganizationType } from '@/auth/types';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { BusinessOperatorSelect } from '@/features/masters/components/BusinessOperatorSelect';
import { errorMessage } from '@/lib/api';
import { formatDate, formatDateTime } from '@/lib/format';
import {
  CERT_STATUS_LABELS,
  previewCertificate,
  useDeviceCertificates,
  useRegisterCertificate,
  useRevokeCertificate,
  useUpdateCertificate,
  type CertificatePreview,
  type CertificateStatus,
  type DeviceCertificateDto,
} from './api';
import './security.css';

const STATUS_TONE: Record<CertificateStatus, BadgeTone> = { active: 'success', revoked: 'danger', expired: 'warning', not_yet_valid: 'neutral' };

export function CertStatusBadge({ status }: { status: CertificateStatus }) {
  return (
    <StatusBadge tone={STATUS_TONE[status]} dot>
      {CERT_STATUS_LABELS[status]}
    </StatusBadge>
  );
}

/** 指紋の短縮表示（先頭 6 バイト） */
const shortFp = (fp: string) => `${fp.slice(0, 17)}…`;

/** 期限まで 30 日未満 */
const expiresSoon = (c: DeviceCertificateDto) => c.status === 'active' && new Date(c.notAfter).getTime() - Date.now() < 30 * 86_400_000;

/** S-98 端末証明書（基金のみ） */
export function DeviceCertificatesPage() {
  const list = useDeviceCertificates();
  const [filter, setFilter] = useState<CertificateStatus | ''>('');
  const [dialog, setDialog] = useState<{ kind: 'register' } | { kind: 'edit' | 'revoke'; cert: DeviceCertificateDto } | null>(null);

  const rows = useMemo(() => (list.data?.items ?? []).filter((c) => !filter || c.status === filter), [list.data, filter]);

  if (list.isPending) return <Loading block />;
  if (list.error) return <ErrorState error={list.error} onRetry={() => void list.refetch()} />;
  const data = list.data;
  const current = data.items.find((c) => c.fingerprintSha256 === data.currentFingerprint);

  const columns: Column<DeviceCertificateDto>[] = [
    {
      key: 'deviceName',
      header: '端末名',
      sortable: true,
      render: (c) => (
        <span className="bs-cert-name">
          <strong>{c.deviceName}</strong>
          {c.commonName && c.commonName !== c.deviceName && <small>CN={c.commonName}</small>}
        </span>
      ),
    },
    {
      key: 'organizationType',
      header: '組織',
      width: 130,
      sortable: true,
      render: (c) => (c.organizationType ? `${c.organizationType}${c.businessOperatorName ? `（${c.businessOperatorName}）` : ''}` : '—'),
    },
    { key: 'fingerprint', header: 'SHA-256 指紋', width: 190, render: (c) => <code className="bs-fp" title={c.fingerprint}>{shortFp(c.fingerprint)}</code> },
    {
      key: 'notAfter',
      header: '有効期限',
      width: 110,
      sortable: true,
      render: (c) => <span className={expiresSoon(c) ? 'bs-num bs-warn-text' : 'bs-num'}>{formatDate(new Date(c.notAfter))}</span>,
    },
    { key: 'status', header: '状態', width: 90, align: 'center', sortable: true, render: (c) => <CertStatusBadge status={c.status} /> },
    {
      key: 'lastUsedAt',
      header: '最終利用',
      width: 150,
      sortable: true,
      render: (c) => (c.lastUsedAt ? <span className="bs-num" title={c.lastUsedIp ?? undefined}>{formatDateTime(c.lastUsedAt)}</span> : <span className="bs-muted">未使用</span>),
    },
    { key: 'createdAt', header: '登録日', width: 115, sortable: true, render: (c) => <span className="bs-num">{formatDate(new Date(c.createdAt))}</span> },
    {
      key: 'actions',
      header: '操作',
      width: 160,
      render: (c) => (
        <span className="bs-sec-actions">
          <Button size="sm" icon={<Pencil size={14} />} onClick={() => setDialog({ kind: 'edit', cert: c })}>
            詳細
          </Button>
          {c.status !== 'revoked' && (
            <Button size="sm" variant="danger" icon={<Ban size={14} />} onClick={() => setDialog({ kind: 'revoke', cert: c })} aria-label={`${c.deviceName} を失効`}>
              失効
            </Button>
          )}
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="システム管理"
        title="端末証明書"
        description="本システムを利用できるパソコン（端末）のクライアント証明書を登録・失効します。失効させた端末からは直ちに利用できなくなります。"
        actions={
          <Button variant="primary" icon={<Plus size={16} />} onClick={() => setDialog({ kind: 'register' })}>
            端末証明書を登録
          </Button>
        }
      />

      <div className={data.clientCertRequired ? 'bs-callout bs-callout--info' : 'bs-callout bs-callout--warning'}>
        {data.clientCertRequired ? <MonitorCheck size={18} aria-hidden /> : <AlertTriangle size={18} aria-hidden />}
        <div>
          {data.clientCertRequired ? (
            <strong>端末証明書は必須です。登録済み・有効な証明書を取り込んだ端末だけが利用できます。</strong>
          ) : (
            <strong>
              現在、端末証明書は必須ではありません（<code>CLIENT_CERT_REQUIRED=false</code>）。登録済み端末の利用状況のみ記録しています。
            </strong>
          )}
          <span className="bs-callout__sub">
            この端末：
            {data.currentFingerprint ? (
              current ? (
                <>
                  {current.deviceName}（<CertStatusBadge status={current.status} />）
                </>
              ) : (
                <>
                  未登録の証明書 <code className="bs-fp">{shortFp(data.currentFingerprint.toUpperCase().match(/.{2}/g)!.join(':'))}</code>
                </>
              )
            ) : (
              '証明書は提示されていません'
            )}
          </span>
        </div>
      </div>

      <div className="bs-cert-toolbar">
        <label className="bs-cert-toolbar__filter">
          <span>状態</span>
          <Select
            width="sm"
            placeholder="すべて"
            value={filter}
            onChange={(e) => setFilter(e.target.value as CertificateStatus | '')}
            options={(['active', 'expired', 'revoked'] as const).map((s) => ({ value: s, label: CERT_STATUS_LABELS[s] }))}
          />
        </label>
        <span className="bs-muted">
          {rows.length} 件 ／ 全 {data.items.length} 件
        </span>
      </div>

      <DataTable
        caption="端末証明書一覧"
        columns={columns}
        rows={rows}
        rowKey={(c) => c.id}
        dense
        defaultSort={{ key: 'createdAt', direction: 'desc' }}
        pagination={{ pageSize: 50 }}
        rowClassName={(c) => (c.status === 'active' ? undefined : 'is-muted')}
        empty={<EmptyState compact icon={<Fingerprint />} title="端末証明書は登録されていません" description="発行スクリプトで作成した .crt ファイルを「端末証明書を登録」から登録してください。" />}
      />

      {dialog?.kind === 'register' && <RegisterDialog onClose={() => setDialog(null)} />}
      {dialog?.kind === 'edit' && <EditDialog cert={dialog.cert} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'revoke' && <RevokeDialog cert={dialog.cert} onClose={() => setDialog(null)} />}
    </>
  );
}

/** 組織区分＋事業実施者（任意） */
function OrgFields({
  orgType,
  operatorId,
  onOrgType,
  onOperator,
}: {
  orgType: OrganizationType | '';
  operatorId: number | null;
  onOrgType: (v: OrganizationType | '') => void;
  onOperator: (v: number | null) => void;
}) {
  const { year } = useFiscalYear();
  return (
    <>
      <FormField label="利用する組織" hint="管理用（任意）。判定には使いません">
        <Select
          placeholder="指定しない"
          value={orgType}
          onChange={(e) => {
            onOrgType(e.target.value as OrganizationType | '');
            if (e.target.value !== '漁協') onOperator(null);
          }}
          options={ORGANIZATION_TYPES.map((t) => ({ value: t, label: t }))}
        />
      </FormField>
      <FormField label="事業実施者" hint={orgType === '漁協' ? '漁協の端末の場合に指定' : '組織が漁協の場合のみ'}>
        <BusinessOperatorSelect fiscalYear={year} includeInactive value={operatorId} onChange={onOperator} placeholder="指定しない" disabled={orgType !== '漁協'} />
      </FormField>
    </>
  );
}

/** ファイル（PEM または DER）→ API に渡す文字列 */
async function readCertificateFile(file: File): Promise<string> {
  const buf = new Uint8Array(await file.arrayBuffer());
  const text = new TextDecoder().decode(buf);
  if (text.includes('-----BEGIN')) return text;
  let bin = '';
  buf.forEach((b) => (bin += String.fromCharCode(b)));
  return btoa(bin);
}

function PreviewTable({ p }: { p: CertificatePreview }) {
  return (
    <dl className="bs-cert-preview" data-testid="cert-preview">
      <dt>SHA-256 指紋</dt>
      <dd>
        <code className="bs-fp">{p.fingerprint}</code>
      </dd>
      <dt>サブジェクト</dt>
      <dd>{p.subject}</dd>
      <dt>発行者</dt>
      <dd>{p.issuer}</dd>
      <dt>有効期間</dt>
      <dd className="bs-num">
        {formatDateTime(p.notBefore)} 〜 {formatDateTime(p.notAfter)}
      </dd>
    </dl>
  );
}

function RegisterDialog({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const register = useRegisterCertificate();
  const [pem, setPem] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [preview, setPreview] = useState<CertificatePreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [orgType, setOrgType] = useState<OrganizationType | ''>('');
  const [operatorId, setOperatorId] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);

  const check = async (value: string) => {
    setPreview(null);
    setPreviewError(null);
    if (!value.trim()) return;
    setChecking(true);
    try {
      const p = await previewCertificate(value);
      setPreview(p);
      setDeviceName((n) => n || p.commonName || '');
    } catch (e) {
      setPreviewError(errorMessage(e));
    } finally {
      setChecking(false);
    }
  };

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const v = await readCertificateFile(file);
    setPem(v);
    await check(v);
  };

  const blocked = !preview || preview.isCa || preview.expired || !!preview.duplicate;

  const onSubmit = async () => {
    if (!deviceName.trim()) {
      setNameError('端末名を入力してください');
      return;
    }
    try {
      await register.mutateAsync({ certificate: pem, deviceName: deviceName.trim(), organizationType: orgType || null, businessOperatorId: operatorId, note: note.trim() || null });
      toast.success('端末証明書を登録しました', deviceName);
      onClose();
    } catch (e) {
      toast.error('登録できませんでした', errorMessage(e));
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title="端末証明書の登録"
      footer={
        <>
          <Button onClick={onClose}>キャンセル</Button>
          <Button variant="primary" icon={<Save size={16} />} disabled={blocked} loading={register.isPending} onClick={() => void onSubmit()}>
            登録する
          </Button>
        </>
      }
    >
      <p className="bs-modal-text">
        発行スクリプト（scripts/pki/issue-device-cert.sh）が出力した <code>端末名.crt</code> を選択するか、内容（-----BEGIN CERTIFICATE----- から）を貼り付けてください。
        <strong>.p12 や秘密鍵（.key）は登録しないでください。</strong>
      </p>
      <FormGrid columns={2}>
        <FormField label="証明書ファイル（.crt / .pem / .cer）" span={2}>
          <label className="bs-cert-file">
            <FileUp size={16} aria-hidden />
            <span>{fileName ?? 'ファイルを選択'}</span>
            <input type="file" accept=".crt,.pem,.cer,.der" onChange={(e) => void onFile(e)} aria-label="証明書ファイル" />
          </label>
        </FormField>
        <FormField label="または貼り付け" span={2} error={previewError ?? undefined}>
          <Textarea
            rows={4}
            className="bs-num"
            placeholder="-----BEGIN CERTIFICATE-----"
            value={pem}
            onChange={(e) => setPem(e.target.value)}
            onBlur={() => void check(pem)}
          />
        </FormField>
      </FormGrid>
      {checking && <Loading label="証明書を確認しています" />}
      {preview && (
        <>
          <PreviewTable p={preview} />
          {preview.duplicate && <div className="bs-callout bs-callout--danger">この証明書は既に登録されています（{preview.duplicate.deviceName}）。</div>}
          {preview.isCa && <div className="bs-callout bs-callout--danger">CA 証明書です。端末ごとに発行したクライアント証明書を登録してください。</div>}
          {preview.expired && <div className="bs-callout bs-callout--danger">有効期限が切れています。</div>}
          <FormGrid columns={2}>
            <FormField label="端末名" required error={nameError ?? undefined} hint="管理用の名称（既定は証明書の CN）">
              <TextField value={deviceName} onChange={(e) => { setDeviceName(e.target.value); setNameError(null); }} maxLength={100} />
            </FormField>
            <FormField label="備考">
              <TextField value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="設置場所・利用者など" />
            </FormField>
            <OrgFields orgType={orgType} operatorId={operatorId} onOrgType={setOrgType} onOperator={setOperatorId} />
          </FormGrid>
        </>
      )}
    </Modal>
  );
}

function EditDialog({ cert, onClose }: { cert: DeviceCertificateDto; onClose: () => void }) {
  const toast = useToast();
  const update = useUpdateCertificate();
  const [deviceName, setDeviceName] = useState(cert.deviceName);
  const [orgType, setOrgType] = useState<OrganizationType | ''>(cert.organizationType ?? '');
  const [operatorId, setOperatorId] = useState<number | null>(cert.businessOperatorId);
  const [note, setNote] = useState(cert.note ?? '');

  const onSave = async () => {
    try {
      await update.mutateAsync({ id: cert.id, input: { deviceName: deviceName.trim(), organizationType: orgType || null, businessOperatorId: operatorId, note: note.trim() || null } });
      toast.success('端末証明書を更新しました');
      onClose();
    } catch (e) {
      toast.error('保存できませんでした', errorMessage(e));
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title="端末証明書の詳細"
      footer={
        <>
          <Button onClick={onClose}>閉じる</Button>
          <Button variant="primary" icon={<Save size={16} />} loading={update.isPending} disabled={!deviceName.trim()} onClick={() => void onSave()}>
            保存する
          </Button>
        </>
      }
    >
      <dl className="bs-cert-preview">
        <dt>状態</dt>
        <dd>
          <CertStatusBadge status={cert.status} />
          {cert.revokedAt && (
            <span className="bs-muted">
              {' '}
              {formatDateTime(cert.revokedAt)} 失効（理由：{cert.revokedReason}）
            </span>
          )}
        </dd>
        <dt>SHA-256 指紋</dt>
        <dd>
          <code className="bs-fp">{cert.fingerprint}</code>
        </dd>
        <dt>サブジェクト</dt>
        <dd>{cert.subject ?? '—'}</dd>
        <dt>発行者</dt>
        <dd>{cert.issuer ?? '—'}</dd>
        <dt>シリアル番号</dt>
        <dd className="bs-num">{cert.serialNumber ?? '—'}</dd>
        <dt>有効期間</dt>
        <dd className="bs-num">
          {formatDateTime(cert.notBefore)} 〜 {formatDateTime(cert.notAfter)}
        </dd>
        <dt>最終利用</dt>
        <dd className="bs-num">{cert.lastUsedAt ? `${formatDateTime(cert.lastUsedAt)}（${cert.lastUsedIp ?? '—'}）` : '未使用'}</dd>
        <dt>登録日時</dt>
        <dd className="bs-num">{formatDateTime(cert.createdAt)}</dd>
      </dl>
      <FormGrid columns={2}>
        <FormField label="端末名" required>
          <TextField value={deviceName} onChange={(e) => setDeviceName(e.target.value)} maxLength={100} />
        </FormField>
        <FormField label="備考">
          <TextField value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
        </FormField>
        <OrgFields orgType={orgType} operatorId={operatorId} onOrgType={setOrgType} onOperator={setOperatorId} />
      </FormGrid>
    </Modal>
  );
}

const REVOKE_REASONS = ['端末の紛失・盗難', '端末の廃棄・入れ替え', '利用者の異動・退職', '証明書の再発行'];

function RevokeDialog({ cert, onClose }: { cert: DeviceCertificateDto; onClose: () => void }) {
  const toast = useToast();
  const revoke = useRevokeCertificate();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const onRevoke = async () => {
    if (!reason.trim()) {
      setError('失効の理由を入力してください');
      return;
    }
    try {
      await revoke.mutateAsync({ id: cert.id, reason: reason.trim() });
      toast.success('端末証明書を失効させました', `${cert.deviceName} からは利用できなくなりました。`);
      onClose();
    } catch (e) {
      toast.error('失効できませんでした', errorMessage(e));
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="端末証明書を失効させますか？"
      footer={
        <>
          <Button onClick={onClose}>キャンセル</Button>
          <Button variant="danger" icon={<Ban size={16} />} loading={revoke.isPending} onClick={() => void onRevoke()}>
            失効させる
          </Button>
        </>
      }
    >
      <p className="bs-modal-text">
        <strong>{cert.deviceName}</strong>（<code className="bs-fp">{shortFp(cert.fingerprint)}</code>）を失効させます。
        この端末からは<strong>直ちに</strong>利用できなくなり、元に戻すことはできません（再度利用する場合は証明書を発行し直して登録します）。
      </p>
      <FormField label="失効の理由" required error={error ?? undefined}>
        <TextField
          data-autofocus
          list="revoke-reasons"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            setError(null);
          }}
          maxLength={200}
          placeholder="例）端末の紛失・盗難"
        />
      </FormField>
      <datalist id="revoke-reasons">
        {REVOKE_REASONS.map((r) => (
          <option key={r} value={r} />
        ))}
      </datalist>
    </Modal>
  );
}
